import { useEffect, useMemo, useState } from 'react';
import adminApi from '@/lib/adminApi';

const editablePages = [
  { page_key: 'home', label: 'Home', path: '/' },
  { page_key: 'about', label: 'About', path: '/about' },
  { page_key: 'contact', label: 'Contact', path: '/contact' },
  { page_key: 'artists', label: 'Artists Directory', path: '/artists' },
  { page_key: 'collaborators', label: 'Collaborators Directory', path: '/collaborators' },
  { page_key: 'ai_studio', label: 'AI Studio', path: '/ai-studio' },
  { page_key: 'competitions', label: 'Competitions', path: '/competitions' },
  { page_key: 'mobile_app', label: 'Mobile App', path: '/mobile-app' },
  { page_key: 'privacy_policy', label: 'Privacy Policy', path: '/privacy-policy' },
  { page_key: 'terms', label: 'Terms & Conditions', path: '/terms-and-conditions' },
  { page_key: 'trust_safety', label: 'Trust & Safety', path: '/trust-safety' },
  { page_key: 'media_kit', label: 'Media Kit', path: '/media-kit' },
  { page_key: 'faq', label: 'FAQ', path: '/faq' },
];

const defaultSeo = {
  home: {
    title: 'Post, Connect, Get Work',
    description: 'Kalamanch.art helps Indian artists post profiles, photos, videos, and portfolios so collaborators and clients can discover them.',
    keywords: 'Kalamanch, Indian artists, creative talent India, artist portfolio',
  },
  about: {
    title: 'About Kalamanch.art',
    description: 'Learn how Kalamanch.art connects Indian artists with collaborators, organizers, production houses, institutions, and creative opportunities.',
    keywords: 'about Kalamanch, Indian creative marketplace, artist discovery platform',
  },
  contact: {
    title: 'Contact Kala Manch',
    description: 'Contact Kala Manch for artist verification, collaborator onboarding, media review, support, profile correction, and safety concerns.',
    keywords: 'Kala Manch contact, artist support, collaborator support, profile correction',
  },
  ai_studio: {
    title: 'KalaManch AI Studio for Artists',
    description: 'Use KalaManch AI Studio to generate artist bios, captions, hashtags, reel scripts, event announcements, poster copy, and artist statements.',
    keywords: 'AI tools for artists, artist caption generator, reel script generator, Kalamanch AI Studio',
  },
  competitions: {
    title: 'KalaManch Competitions',
    description: 'Discover live, upcoming, and past KalaManch competitions for Indian artists, performers, writers, musicians, dancers, and visual creators.',
    keywords: 'KalaManch competitions, artist competitions India, online art competition, performer contests',
  },
  mobile_app: {
    title: 'KalaManch Mobile App',
    description: 'KalaManch mobile app for artists and collaborators with profiles, discovery, PMB messages, portfolio tools, and AI Studio.',
    keywords: 'KalaManch app, artist app India, collaborator app, AI Studio mobile',
  },
};

const fieldGuidance = {
  hero_tagline: 'Small label above the hero heading. Keep it 2-5 words.',
  hero_title: 'Main page heading. Keep it clear, 4-10 words.',
  hero_subtitle: 'Paragraph under the hero heading. Ideal 20-45 words.',
  brand_heading: 'Home brand partner section heading. Ideal 3-8 words.',
  brand_description: 'Home brand partner intro text. Ideal 20-45 words.',
  cta_heading: 'Home final call-to-action heading. Ideal 4-9 words.',
  cta_description: 'Home final call-to-action paragraph. Ideal 18-40 words.',
  banner_title: 'Optional page banner heading. Use for current campaign or featured competition.',
  banner_subtitle: 'Optional page banner description. Ideal 15-40 words.',
  banner_image_url: 'Optional banner image URL. Leave blank to hide image area.',
  banner_link_url: 'Optional banner button link. Use a full URL or site path.',
  banner_active: 'Use yes to show banner, no to hide it.',
  challenge_status: 'Competitions page challenge label. Example: Upcoming - Dates to be announced.',
  theme_title: 'Competition theme heading. Example: India Through Your Eyes.',
  theme_body: 'Theme explanation. Ideal 30-70 words.',
  sponsor_title: 'Sponsor block heading. Example: Sponsor space.',
  sponsor_body: 'Sponsor placeholder or sponsor details. Ideal 15-50 words.',
  allowed_categories: 'Competition allowed categories. One per line.',
  not_allowed_items: 'Competition disallowed items. One per line.',
  verification_title: 'Shortlisted verification heading.',
  verification_body: 'Explain that creation proof is required only after shortlisting.',
  proof_items: 'Accepted creation proof items. One per line.',
  recognition_tiers: 'Recognition tiers. One per line using Count | Label. Example: Top 25 | Shortlisted Artists.',
  recognition_note: 'Note about prizes, sponsors, certificates, and visibility.',
  referral_title: 'Referral bonus heading.',
  referral_body: 'Referral bonus explanation. Mention that referral is not mandatory.',
  social_caption: 'Ready-to-use caption for artists. Keep @artist placeholders if useful.',
  vision_title: 'About page vision section heading. Ideal 3-8 words.',
  vision_body: 'About page first vision paragraph. Ideal 35-80 words.',
  vision_body_2: 'About page second vision paragraph. Ideal 35-80 words.',
  cta_title: 'Final section heading. Ideal 4-9 words.',
  email: 'Contact page email shown to visitors.',
  location: 'Contact page location text. Keep it short.',
  form_heading: 'Contact form section heading. Ideal 3-7 words.',
  form_description: 'Contact form helper text. Ideal 15-35 words.',
  directory_note: 'Short note near directory filters or intro. Ideal 12-30 words.',
  seasonal_themes: 'Artists page seasonal discovery chips. One per line: Label | search keywords. Example: Wedding Season | wedding sangeet mehendi singer anchor dancer band. Leave blank to use Kalamanch default festival, school, society, wedding, corporate, and open-mic chips.',
  policy_notice: 'AI Studio safety notice. Ideal 25-60 words.',
  beta_note: 'AI Studio beta/credit notice. Ideal 15-35 words.',
  android_status: 'Mobile app Android status card. Ideal 10-25 words.',
  ios_status: 'Mobile app iOS status card. Ideal 10-25 words.',
  android_store_url: 'Full Play Store URL. Leave blank until live.',
  ios_store_url: 'Full App Store URL. Leave blank until live.',
  features_title: 'Mobile app features section heading. Ideal 3-8 words.',
  features_body: 'Mobile app features paragraph/list. Ideal 40-100 words.',
  ai_tools_title: 'Mobile app AI tools section heading. Ideal 3-8 words.',
  ai_tools_body: 'Mobile app AI tools paragraph/list. Ideal 40-100 words.',
  body: 'Main managed page content. Use short paragraphs; maximum 1200 characters for now.',
  download_label: 'Media kit download button text. Ideal 2-5 words.',
  download_url: 'Full URL or site path for the media kit PDF.',
};

const AdminContentSeo = () => {
  const [pageKey, setPageKey] = useState('home');
  const [allowedFields, setAllowedFields] = useState({});
  const [contentForms, setContentForms] = useState({});
  const [seoForms, setSeoForms] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingContent, setSavingContent] = useState(false);
  const [savingSeo, setSavingSeo] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const selectedPage = useMemo(() => editablePages.find((page) => page.page_key === pageKey) || editablePages[0], [pageKey]);
  const fields = allowedFields[pageKey] || [];
  const contentForm = contentForms[pageKey] || { sections: {} };
  const seoForm = seoForms[pageKey] || { ...selectedPage, ...(defaultSeo[pageKey] || {}) };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [contentResponse, seoResponse] = await Promise.all([
          adminApi.get('/admin/content-pages'),
          adminApi.get('/admin/seo'),
        ]);
        if (!mounted) return;
        const nextContent = {};
        editablePages.forEach((page) => {
          const stored = (contentResponse.data.pages || []).find((item) => item.page_key === page.page_key);
          nextContent[page.page_key] = stored || { ...page, sections: {}, is_active: true };
        });
        const nextSeo = {};
        editablePages.forEach((page) => {
          const stored = (seoResponse.data.records || []).find((item) => item.page_key === page.page_key);
          nextSeo[page.page_key] = stored || { ...page, ...(defaultSeo[page.page_key] || {}), is_active: true };
        });
        setAllowedFields(contentResponse.data.allowed_fields || {});
        setContentForms(nextContent);
        setSeoForms(nextSeo);
      } catch (requestError) {
        setError(requestError.response?.data?.detail || 'Unable to load content and SEO settings.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const updateContentField = (field, value) => {
    setContentForms((current) => ({
      ...current,
      [pageKey]: {
        ...contentForm,
        ...selectedPage,
        is_active: contentForm.is_active ?? true,
        sections: { ...(contentForm.sections || {}), [field]: value },
      },
    }));
  };

  const updateSeoField = (field, value) => {
    setSeoForms((current) => ({
      ...current,
      [pageKey]: {
        ...seoForm,
        ...selectedPage,
        [field]: value,
      },
    }));
  };

  const saveContent = async () => {
    setSavingContent(true);
    setStatus('');
    setError('');
    try {
      const payload = { ...selectedPage, ...contentForm, page_key: pageKey, path: selectedPage.path };
      const response = await adminApi.put(`/admin/content-pages/${pageKey}`, payload);
      setContentForms((current) => ({ ...current, [pageKey]: response.data.page }));
      setStatus('Page content saved safely.');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to save page content.');
    } finally {
      setSavingContent(false);
    }
  };

  const saveSeo = async () => {
    setSavingSeo(true);
    setStatus('');
    setError('');
    try {
      const payload = { ...selectedPage, ...seoForm, page_key: pageKey, path: selectedPage.path };
      const response = await adminApi.put(`/admin/seo/${pageKey}`, payload);
      setSeoForms((current) => ({ ...current, [pageKey]: response.data.seo }));
      setStatus('SEO saved.');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to save SEO.');
    } finally {
      setSavingSeo(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#FF6F00]">Manage content and SEO</p>
        <h2 className="mt-2 text-3xl font-bold text-gray-900">Safe page editing</h2>
        <p className="mt-3 max-w-3xl text-gray-600">
          Edit approved text and metadata only. Layout, spacing, buttons, and cards stay controlled by the website so admin content cannot break mobile or desktop design.
        </p>
      </section>

      {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {status && <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{status}</div>}
      {loading && <div className="text-gray-500">Loading content tools...</div>}

      {!loading && (
        <>
          <section className="rounded-[2rem] border border-orange-100 bg-white p-5 shadow-lg">
            <label className="text-sm font-bold text-gray-900">Page</label>
            <select value={pageKey} onChange={(event) => setPageKey(event.target.value)} className="mt-2 w-full rounded-2xl border border-orange-100 px-4 py-3">
              {editablePages.map((page) => (
                <option key={page.page_key} value={page.page_key}>{page.label} ({page.path})</option>
              ))}
            </select>
          </section>

          <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Page content</h3>
                <p className="mt-1 text-sm text-gray-500">Blank fields fall back to the current hard-coded website copy.</p>
              </div>
              <button type="button" onClick={saveContent} disabled={savingContent} className="btn-primary disabled:bg-gray-300">
                {savingContent ? 'Saving...' : 'Save content'}
              </button>
            </div>
            <div className="mt-6 grid gap-4">
              {fields.map((field) => (
                <label key={field} className="block">
                  <span className="text-sm font-bold capitalize text-gray-800">{field.replaceAll('_', ' ')}</span>
                  <span className="mt-1 block text-xs leading-5 text-gray-500">{fieldGuidance[field] || 'Safe text field controlled by the page layout.'}</span>
                  <textarea
                    value={(contentForm.sections || {})[field] || ''}
                    onChange={(event) => updateContentField(field, event.target.value)}
                    rows={field.includes('body') || field.includes('description') ? 4 : 2}
                    className="mt-2 w-full rounded-2xl border border-orange-100 px-4 py-3 text-sm text-gray-800"
                  />
                </label>
              ))}
            </div>
          </section>

          <section className="rounded-[2rem] border border-purple-100 bg-white p-6 shadow-lg sm:p-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">SEO metadata</h3>
                <p className="mt-1 text-sm text-gray-500">Controls browser title, description, keywords, Open Graph, and Twitter metadata for this page.</p>
              </div>
              <button type="button" onClick={saveSeo} disabled={savingSeo} className="btn-primary disabled:bg-gray-300">
                {savingSeo ? 'Saving...' : 'Save SEO'}
              </button>
            </div>
            <div className="mt-6 grid gap-4">
              <input value={seoForm.title || ''} onChange={(event) => updateSeoField('title', event.target.value)} className="rounded-2xl border border-purple-100 px-4 py-3" placeholder="SEO title" />
              <textarea value={seoForm.description || ''} onChange={(event) => updateSeoField('description', event.target.value)} rows="3" className="rounded-2xl border border-purple-100 px-4 py-3" placeholder="SEO description" />
              <textarea value={seoForm.keywords || ''} onChange={(event) => updateSeoField('keywords', event.target.value)} rows="2" className="rounded-2xl border border-purple-100 px-4 py-3" placeholder="SEO keywords" />
              <input value={seoForm.image_url || ''} onChange={(event) => updateSeoField('image_url', event.target.value)} className="rounded-2xl border border-purple-100 px-4 py-3" placeholder="Optional social share image URL" />
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default AdminContentSeo;
